import { useHandler } from "@redotech/react-util/hook";
import type { MetafieldValueMatchKind } from "@redotech/redo-conversion-core-common/checkouts/product-metafields";
import type { RcoProductMetafieldsSegmentSchema } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ShopifyIdType } from "@redotech/shopify-util/id";
import { memo, useMemo, useState } from "react";

type Metafield = RcoProductMetafieldsSegmentSchema["metafields"][number];

export const ProductMetafieldsSelector = memo(
  function ProductMetafieldsSelector({
    metafields,
    onMetafieldsChanged,
  }: {
    metafields: Metafield[];
    onMetafieldsChanged: (metafields: Metafield[]) => void;
  }) {
    const { t } = useTranslation();
    const [namespace, setNamespace] = useState("");
    const [key, setKey] = useState("");
    const [value, setValue] = useState("");
    const [valueMatchKind, setValueMatchKind] =
      useState<MetafieldValueMatchKind>("exact");
    const [resourceType, setResourceType] = useState<
      ShopifyIdType.Product | ShopifyIdType.ProductVariant
    >(ShopifyIdType.Product);

    const resourceTypeOptions: TextRedoListItem<
      ShopifyIdType.Product | ShopifyIdType.ProductVariant
    >[] = useMemo(
      () => [
        {
          type: "text",
          id: ShopifyIdType.Product,
          value: ShopifyIdType.Product,
          text: t("Product metafield"),
        },
        {
          type: "text",
          id: ShopifyIdType.ProductVariant,
          value: ShopifyIdType.ProductVariant,
          text: t("Variant metafield"),
        },
      ],
      [t],
    );

    const selectedResourceTypeOption = useMemo(() => {
      return resourceTypeOptions.find(
        (option) => option.value === resourceType,
      );
    }, [resourceType, resourceTypeOptions]);

    const matchKindOptions: TextRedoListItem<MetafieldValueMatchKind>[] =
      useMemo(
        () => [
          { type: "text", id: "exact", value: "exact", text: t("Exact match") },
          {
            type: "text",
            id: "partial",
            value: "partial",
            text: t("Contains (partial match)"),
          },
        ],
        [t],
      );

    const selectedMatchKindOption = useMemo(() => {
      return matchKindOptions.find((option) => option.value === valueMatchKind);
    }, [valueMatchKind, matchKindOptions]);

    const handleAddMetafield = useHandler(() => {
      if (!namespace.trim() || !key.trim() || !value.trim()) {
        return;
      }

      const newMetafield: Metafield = {
        resourceType,
        namespace: namespace.trim(),
        key: key.trim(),
        value: value.trim(),
        valueMatchKind,
      };

      // Check if metafield already exists
      const exists = metafields.some(
        (m) =>
          m.resourceType === newMetafield.resourceType &&
          m.namespace === newMetafield.namespace &&
          m.key === newMetafield.key &&
          m.value === newMetafield.value &&
          m.valueMatchKind === newMetafield.valueMatchKind,
      );

      if (!exists) {
        onMetafieldsChanged([...metafields, newMetafield]);
      }

      // Clear form
      setNamespace("");
      setKey("");
      setValue("");
      setValueMatchKind("exact");
    });

    const handleRemoveMetafield = useHandler((index: number) => {
      const newMetafields = metafields.filter((_, i) => i !== index);
      onMetafieldsChanged(newMetafields);
    });

    return (
      <Flex dir="column" gap="md" w="full">
        <Flex dir="column" gap="sm" w="full">
          <RedoSingleSelectDropdownInput<
            ShopifyIdType.Product | ShopifyIdType.ProductVariant
          >
            label={t("Resource type")}
            options={resourceTypeOptions}
            optionSelected={({ item }) => setResourceType(item.value)}
            selectedItem={selectedResourceTypeOption}
            size="xs"
          />
          <RedoTextInput
            label={t("Namespace")}
            setValue={setNamespace}
            size="sm"
            value={namespace}
          />
          <RedoTextInput
            label={t("Key")}
            setValue={setKey}
            size="sm"
            value={key}
          />
          <RedoTextInput
            label={t("Value")}
            setValue={setValue}
            size="sm"
            value={value}
          />
          <RedoSingleSelectDropdownInput<MetafieldValueMatchKind>
            label={t("Match type")}
            options={matchKindOptions}
            optionSelected={({ item }) => setValueMatchKind(item.value)}
            selectedItem={selectedMatchKindOption}
            size="xs"
          />
          <RedoButton
            disabled={!namespace.trim() || !key.trim() || !value.trim()}
            hierarchy="secondary"
            onClick={handleAddMetafield}
            size="xs"
            text={t("Add metafield")}
          />
        </Flex>

        {metafields.length > 0 && (
          <Flex dir="column" gap="sm" w="full">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Selected Metafields ({{count}})", {
                count: metafields.length,
              })}
            </Text>
            {metafields.map((metafield, index) => (
              <Flex
                align="center"
                borderBottomWidth="1px"
                borderColor="primary"
                borderStyle="solid"
                dir="row"
                gap="sm"
                justify="space-between"
                key={index}
                pb="xs"
                w="full"
              >
                <Flex dir="column" gap="xs">
                  <Text fontSize="xs" fontWeight="medium" textColor="primary">
                    {metafield.resourceType === t("Product")
                      ? t("Product")
                      : t("Variant")}
                    : {metafield.namespace}.{metafield.key}
                  </Text>
                  <Text fontSize="xs" textColor="secondary">
                    Value: {metafield.value} (
                    {metafield.valueMatchKind === "partial"
                      ? "contains"
                      : "exact"}
                    )
                  </Text>
                </Flex>
                <RedoButton
                  aria-label={t("Remove")}
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={() => handleRemoveMetafield(index)}
                  size="xs"
                />
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    );
  },
);
